import './transfer-form.ajscss';

import { IAgentConfigDeprecatedService } from '@agent/core/agent/agent-config-deprecated.service';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { isResponseId, ITransfer, TransferTarget } from '@agent/core/contact/transfer/models/transfer';
import { ITransferDeprecatedService } from '@agent/core/contact/transfer/transfer-deprecated.service';
import { IDirectoryService } from '@agent/core/search/directory.service';
import { DEFAULT_MAX_SEARCH_RESULTS, ISearchService } from '@agent/core/search/search.service';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { IComponentController, IComponentOptions, IPromise } from 'angular';

import { ChannelDirection, ChannelType, Group, LoaderDeprecatedService, ProfileFacade, Skill, Team } from '@contact/upgrade';

import templateUrl from './transfer-form.html';

const MODULE_NAME = 'transferForm';
const LOADER_TARGET_ELT_ID = 'transferForm';

export type CsrFilter = {
  csrPrefix?: string;
  teamId?: string;
  groupId?: string;
  skillIds?: string[];
};

class TransferFormCtrl implements IComponentController {
  public name = MODULE_NAME;
  public ChannelType = ChannelType;
  public TransferTarget = TransferTarget;

  // binding
  public transfer: ITransfer;
  public eventTransferChange: Function;

  // internal properties
  public skills: Skill[];
  public teams: Team[];
  public groups: Group[];
  public csrs: any[];
  public contactSkills: string[];
  public noResultsTeamTarget: boolean = false;
  public skillsFilter: any[] = [];
  public availableTeams: any[] = [];

  public isMaxCsrListReach: boolean = false;
  private previousFilter: CsrFilter;
  private isMail: boolean = false;
  private _visibleDomainUuids: string[] = [];

  constructor(
    private readonly sessionDeprecatedService: SessionDeprecatedService,
    private readonly agentConfigDeprecatedService: IAgentConfigDeprecatedService,
    private readonly transferDeprecatedService: ITransferDeprecatedService,
    private readonly search: ISearchService,
    public readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly directoryService: IDirectoryService,
    protected readonly loaderDeprecatedService: LoaderDeprecatedService,
    protected readonly profileFacade: ProfileFacade
  ) {
    this.skills = [];
    this.teams = [];
    this.groups = [];
    this.csrs = [];
    this.contactSkills = [];
  }

  $onInit(): void {
    // skill available
    this.isMail = this.transfer.channel.type === ChannelType.MAIL;
    this.profileFacade.profile$.subscribe(profile => (this._visibleDomainUuids = profile.accessibleDomainUuids));
    this.transferDeprecatedService.getAvailableSkills(this.transfer.key, !this.isMail).then(skills => {
      if (this.isMail) {
        (skills as any[]).forEach(s => {
          s.cssClass = '';
        });
      }
      this.skills = [...skills].sort((a, b) => {
        if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
        if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
        return 0;
      });
    });
    // domain teams available
    this.agentConfigDeprecatedService.domainTeams.forEach(team => this.teams.push(team));
    // domain group available
    this.agentConfigDeprecatedService.domainGroups.forEach(group => this.groups.push(group));
    // contact skills
    if (isResponseId(this.transfer.key)) {
      this.contactDeprecatedService.getContact(this.transfer.key).then(contact => (this.contactSkills = contact.skills));
    }
  }

  updateCandidateCsrs(payload: CsrFilter) {
    // if same filter as previous don't make call as we listen now keyup and can be called on update with same filter value
    if (
      !!this.previousFilter &&
      this.previousFilter.csrPrefix === payload.csrPrefix &&
      this.previousFilter.groupId === payload.groupId &&
      this.previousFilter.teamId === payload.teamId &&
      this.previousFilter.skillIds === payload.skillIds
    ) {
      return;
    }
    let csr = (payload.csrPrefix && payload.csrPrefix.toLowerCase()) || '';
    let skills = payload.skillIds || [];
    this.activeLoader();

    let csrListPromise: IPromise<any[]>;

    if (this.isMail) {
      csrListPromise = this.search.searchCsr(
        payload.teamId || '',
        payload.groupId || '',
        skills,
        csr,
        'MAIL_INCOMING',
        true,
        this._visibleDomainUuids
      );
    } else {
      csrListPromise = this.search.searchCsr(
        payload.teamId || '',
        payload.groupId || '',
        skills,
        csr,
        undefined,
        undefined,
        this._visibleDomainUuids
      );
    }

    csrListPromise.then((csrList: any) => {
      let candidateCsrs: any[] = csrList.map(c => ({
        name: c.lastName + ' ' + c.firstName,
        email: c.mail,
        uuid: c.uuid,
      }));
      this.isMaxCsrListReach = candidateCsrs.length >= DEFAULT_MAX_SEARCH_RESULTS;
      this.csrs = candidateCsrs
        .filter(c => c.uuid !== this.sessionDeprecatedService.getProfile().id)
        .sort((a, b) => {
          let nameA = a.name.toUpperCase();
          let nameB = b.name.toUpperCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
      this.disposeLoader();
    });
  }

  updateCandicateTeamList(): void {
    this.transfer.target = null;
    this.noResultsTeamTarget = false;
    this.availableTeams = [];

    this.activeLoader();
    this.directoryService
      .getInternalCorrespondentsWithTeam(this.sessionDeprecatedService.getProfile().domainId, this.skillsFilter, '', {
        type: ChannelType.MAIL,
        direction: ChannelDirection.INCOMING,
      })
      .then(success => {
        if (success.data.length !== 0) {
          let teamMap = {};
          let tmpArray: any[] = [];
          success.data
            .map((agent: any): any => ({
              id: agent.teams[0].uuid,
              name: agent.teams[0].name,
              type: 'team',
              skills: this.skillsFilter,
            }))
            .map(t => {
              if (!teamMap.hasOwnProperty(t.id)) {
                teamMap[t.id] = true;
                tmpArray.push(t);
              }
            });
          this.availableTeams = [...tmpArray].sort((a, b) => {
            if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
            if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
            return 0;
          });
        } else {
          this.noResultsTeamTarget = true;
          this.availableTeams = [];
        }
        this.disposeLoader();
      });

    this.eventTransferChange({ $event: { transfer: this.transfer } });
  }

  teamChanged(): void {
    this.eventTransferChange({ $event: { transfer: this.transfer } });
  }

  onTargetTypeChange({ targetType }): void {
    this.transfer.targetType = targetType;
    this.transfer.target = null;
    // add source depend of target type
    switch (targetType) {
      case TransferTarget.SKILL:
      case TransferTarget.TEAM:
        this.transfer.source = this.contactSkills;
        break;
      default:
        this.transfer.source = undefined;
        break;
    }
    this.eventTransferChange({ $event: { transfer: this.transfer } });
  }

  onTargetChange({ target }): void {
    this.transfer.target = target;
    this.eventTransferChange({ $event: { transfer: this.transfer } });
  }

  onCommentChange({ comment }): void {
    this.transfer.comment = comment;
    this.eventTransferChange({ $event: { transfer: this.transfer } });
  }

  private activeLoader() {
    this.loaderDeprecatedService.active(LOADER_TARGET_ELT_ID);
  }

  private disposeLoader() {
    this.loaderDeprecatedService.dispose(LOADER_TARGET_ELT_ID);
  }
}

export class TransferForm implements IComponentOptions {
  templateUrl = templateUrl;
  bindings = {
    transfer: '<',
    eventTransferChange: '&',
  } as { [binding: string]: string };
  controller = [
    'sessionDeprecatedService',
    'agentConfigDeprecatedService',
    'transferDeprecatedService',
    'search',
    'contactDeprecatedService',
    'directory',
    'loaderDeprecatedService',
    'profileFacade',
    TransferFormCtrl,
  ];
}

export default MODULE_NAME;
